// ─────────────────────────────────────────────────────────────────────────────
// The MTN Shop — AI Social Media Hub (SocialPopAdmin)
// Shared types, auth, constants and data helpers.
// Standalone from the Zera admin realms: its own login, tables and storage.
// The Social Media Hub section reuses the shared six-step Brand Studio engine,
// scoped to the isolated `mtnshop` data realm (see src/lib/adminAuth.ts).
// ─────────────────────────────────────────────────────────────────────────────
import { supabase } from '@/lib/supabase';
import { ADMIN_CREDENTIALS, setAdminRealm } from '@/lib/adminAuth';

// ---- Auth (app-level gate, same pattern as the other admin portals) ----
// Credentials live in ADMIN_CREDENTIALS (single source of truth): the
// `mtnshop` entry = ShopSocialAdmin / ShopSocialAdmin. Old credentials removed.
export const MTN_REALM = 'mtnshop' as const;
export const MTN_HUB_CREDENTIALS = ADMIN_CREDENTIALS.mtnshop;

// New session key so any session created under old credentials is invalidated.
const SESSION_KEY = 'socialpop_session_v2';
const LEGACY_SESSION_KEYS = ['socialpop_session'];

function clearLegacySessions(): void {
  try { LEGACY_SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k)); } catch { /* ignore */ }
}

/**
 * Activates the isolated `mtnshop` data realm for this tab so the Social
 * Media Hub (shared Brand Studio engine) reads/writes only MTN data.
 */
export function activateMtnRealm(): void {
  setAdminRealm(MTN_REALM);
}

export function mtnLogin(username: string, password: string): boolean {
  clearLegacySessions();
  if (username === MTN_HUB_CREDENTIALS.username && password === MTN_HUB_CREDENTIALS.password) {
    try { sessionStorage.setItem(SESSION_KEY, 'authenticated'); } catch { /* ignore */ }
    activateMtnRealm();
    return true;
  }
  return false;
}

export function mtnIsAuthed(): boolean {
  clearLegacySessions();
  try { return sessionStorage.getItem(SESSION_KEY) === 'authenticated'; } catch { return false; }
}

export function mtnLogout(): void {
  clearLegacySessions();
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}



// ---- Brand constants ----
export const MTN_BRAND = {
  name: 'The MTN Shop',
  handle: 'shopwithmtn',
  website: 'https://shopmtn.com',
  instagram: 'https://www.instagram.com/shopwithmtn/',
  // Official MTN SHOP™ logo — stencil wordmark on matte black with the
  // multicolor mountain ridge. Used as the @shopwithmtn profile picture and
  // as the brand mark everywhere in the MTN hub UI.
  avatar: 'https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1783426723545_b965b815.png',
};

// ---- Campaign types (Campaign Studio) ----
// Each generated campaign is built around one of these focus types; the AI
// follows the style and creative direction of the chosen type.
export interface CampaignTypeDef {
  id: 'product' | 'brand' | 'sale' | 'theme' | 'promotion';
  label: string;
  tagline: string;
  focusLabel: string;
  focusPlaceholder: string;
  suggestions: string[];
  tone: 'sky' | 'emerald' | 'amber' | 'fuchsia' | 'red';
}

export const CAMPAIGN_TYPES: CampaignTypeDef[] = [
  {
    id: 'product', label: 'Product', tagline: 'Every post orbits one hero product or product line',
    focusLabel: 'Hero product or line', focusPlaceholder: 'e.g. CM Lodestar Chain Hoist',
    suggestions: ['CM Lodestar Chain Hoist', 'Tower Lifts', 'Truss & Accessories', 'Fall Protection Harnesses', 'Rigging Hardware'],
    tone: 'sky',
  },
  {
    id: 'brand', label: 'Brand', tagline: 'Story-driven affinity for The MTN Shop itself',
    focusLabel: 'Brand story angle', focusPlaceholder: 'e.g. Safety-first culture, Built by riggers for riggers',
    suggestions: ['Safety-First Culture', 'Built for the Crew', 'Behind the Scenes at MTN', 'Why Pros Trust MTN', 'Our Story'],
    tone: 'emerald',
  },
  {
    id: 'sale', label: 'Sale', tagline: 'Conversion-driven urgency arc around a sale',
    focusLabel: 'Sale to promote', focusPlaceholder: 'e.g. Spring Rigging Sale — 20% off hoists',
    suggestions: ['Seasonal Sale', 'Clearance Event', 'Hoist Deals Week', 'PPE Price Drop', 'Bundle & Save'],
    tone: 'red',
  },
  {
    id: 'theme', label: 'Theme', tagline: 'Editorial series exploring one theme from every angle',
    focusLabel: 'Campaign theme', focusPlaceholder: 'e.g. Rigging Safety Week',
    suggestions: [
      'Product Spotlight Week', 'Rigging Safety Week', 'Tool Tuesday Series', 'Stage Building Essentials',
      'New Arrival Week', 'Best Sellers Week', 'Chain Hoist Week', 'Tower Lift Week', 'Truss Accessories Week',
      'PPE Week', 'Live Event Gear Week', 'Equipment Maintenance Week', 'Education Week', 'Customer Problem Solver Week',
    ],
    tone: 'fuchsia',
  },
  {
    id: 'promotion', label: 'Promotion', tagline: 'Tease → reveal → convert one specific promotion or launch',
    focusLabel: 'Promotion or launch', focusPlaceholder: 'e.g. New arrival launch, giveaway, trade-show booth',
    suggestions: ['New Arrival Launch', 'Giveaway', 'Trade Show Presence', 'Financing Offer', 'Free Shipping Event'],
    tone: 'amber',
  },
];

export const campaignTypeOf = (id: string | null | undefined): CampaignTypeDef =>
  CAMPAIGN_TYPES.find(t => t.id === id) || CAMPAIGN_TYPES[3];

// Legacy weekly theme presets (kept for the Theme campaign type suggestions)
export const WEEKLY_THEMES = CAMPAIGN_TYPES[3].suggestions;


// ---- AI Brain training input kinds ----
export interface BrainKind { id: string; label: string; hint: string; }
export const BRAIN_KINDS: BrainKind[] = [
  { id: 'product', label: 'Product', hint: 'Product names, descriptions and details the AI should know' },
  { id: 'promotion', label: 'Weekly Promotion', hint: 'Active or upcoming promotions and sales' },
  { id: 'initiative', label: 'Company Initiative', hint: 'Company programs, values, launches' },
  { id: 'website_link', label: 'Website Link', hint: 'Pages the AI should reference' },
  { id: 'social_profile', label: 'Social Profile Link', hint: 'Social accounts to study' },
  { id: 'social_post', label: 'Social Post Link', hint: 'Specific posts to learn from' },
  { id: 'campaign_past', label: 'Past Campaign', hint: 'Campaigns that already ran (what worked)' },
  { id: 'campaign_idea', label: 'Campaign Idea', hint: 'Upcoming campaign concepts' },
  { id: 'keywords', label: 'Keywords', hint: 'Keywords and phrases to weave in' },
  { id: 'voice_note', label: 'Brand Voice Note', hint: 'How The MTN Shop speaks' },
  { id: 'audience', label: 'Audience Description', hint: 'Who the content targets' },
  { id: 'hashtags', label: 'Hashtag Examples', hint: 'Hashtag sets that match the brand' },
  { id: 'caption_example', label: 'Caption Example', hint: 'Captions that nail the voice' },
  { id: 'approved_example', label: 'Approved Post Example', hint: 'Posts approved in the past' },
  { id: 'rejected_example', label: 'Rejected Post Example', hint: 'Posts to avoid repeating' },
  { id: 'sales_image', label: 'Sales Image', hint: 'Promo image links / descriptions' },
];

// ---- Brand voice profile shape ----
export interface VoiceProfile {
  writing_style: string;
  tone: string;
  technical_level: number;   // 0-100
  sales_level: number;
  educational_level: number;
  humor_level: number;
  caption_length: string;    // short | medium | long
  hashtag_style: string;
  cta_style: string;
  approved_phrases: string;
  avoid_words: string;
  product_messaging: string;
  audience: string;
  vocabulary: string;
}

export const DEFAULT_VOICE: VoiceProfile = {
  writing_style: 'Direct, expert, practical. Speaks rigger-to-rigger with zero fluff.',
  tone: 'Confident, safety-first, respected trade professional',
  technical_level: 70,
  sales_level: 40,
  educational_level: 65,
  humor_level: 15,
  caption_length: 'medium',
  hashtag_style: 'Trade-specific tags (#rigging #eventproduction) mixed with product tags, 10-15 per post',
  cta_style: 'Clear and action-driven: "Shop the link in bio", "DM us for a quote", "Tag your crew"',
  approved_phrases: 'Built for the job. Rated, tested, trusted. Gear up. Rig it right.',
  avoid_words: 'cheap, hack, guys, insane, crazy deal',
  product_messaging: 'Lead with the problem the gear solves; specs support the story, safety is non-negotiable.',
  audience: 'Production companies, riggers, AV integrators, stagehands, rope-access techs, event producers',
  vocabulary: 'rated capacity, WLL, span, truss, hoist, up-rigger, down-rigger, load-in, show call',
};

// ---- Row types (array-ish columns are JSON strings in text columns) ----
export interface MtnProduct {
  id: string; handle: string | null; name: string; description: string | null;
  category: string | null; vendor: string | null; tags: string | null;
  price: string | null; images: string | null; source_url: string | null;
  status: string; favorite: boolean; popularity: number; created_at: string;
}

export interface MtnWeek {
  id: string; theme: string; strategy: string | null; status: string;
  start_date: string | null; created_at: string;
  campaign_type: string | null; focus: string | null;
}

export interface MtnPost {
  id: string; week_id: string; day_index: number; day_name: string | null;
  post_type: 'feed' | 'story'; title: string | null; caption: string | null;
  hashtags: string | null; cta: string | null; image_prompt: string | null;
  image_url: string | null; product_refs: string | null; story_text: string | null;
  story_layout: string | null; story_stickers: string | null; status: string;
  revision_notes: string | null; created_at: string;
}

export interface MtnBrainInput {
  id: string; kind: string; title: string | null; content: string | null;
  url: string | null; image_url: string | null; status: string; created_at: string;
}

export interface MtnLearningEvent {
  id: string; event_type: string; detail: string | null; created_at: string;
}

export interface StorySticker { type: string; label: string; }

/** Safe JSON parse for the text columns that hold JSON strings. */
export function pj<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

// ---- Data helpers ----
export async function fetchProducts(limit = 1200): Promise<MtnProduct[]> {
  const { data } = await supabase
    .from('mtn_shop_products')
    .select('*')
    .order('favorite', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit);
  return (data || []) as MtnProduct[];
}

export async function fetchWeeks(): Promise<MtnWeek[]> {
  const { data } = await supabase
    .from('mtn_shop_weeks').select('*').order('created_at', { ascending: false }).limit(30);
  return (data || []) as MtnWeek[];
}

export async function fetchPosts(weekId?: string): Promise<MtnPost[]> {
  let q = supabase.from('mtn_shop_posts').select('*').order('day_index').order('post_type');
  if (weekId) q = q.eq('week_id', weekId);
  const { data } = await q.limit(200);
  return (data || []) as MtnPost[];
}

/** Permanently delete individual posts/stories by id (Campaign Studio day-delete + per-card delete). */
export async function deletePostsByIds(ids: string[]): Promise<void> {
  if (!ids.length) return;
  await supabase.from('mtn_shop_posts').delete().in('id', ids);
}

/** Permanently delete an entire campaign week and all of its posts/stories. */
export async function deleteWeekCascade(weekId: string): Promise<void> {
  await supabase.from('mtn_shop_posts').delete().eq('week_id', weekId);
  await supabase.from('mtn_shop_weeks').delete().eq('id', weekId);
}


export async function fetchBrainInputs(): Promise<MtnBrainInput[]> {
  const { data } = await supabase
    .from('mtn_shop_brain_inputs').select('*').order('created_at', { ascending: false }).limit(200);
  return (data || []) as MtnBrainInput[];
}

export async function fetchLearningEvents(limit = 40): Promise<MtnLearningEvent[]> {
  const { data } = await supabase
    .from('mtn_shop_learning_events').select('*').order('created_at', { ascending: false }).limit(limit);
  return (data || []) as MtnLearningEvent[];
}

export async function logEvent(event_type: string, detail: string): Promise<void> {
  await supabase.from('mtn_shop_learning_events').insert({ event_type, detail });
}

export async function fetchVoice(): Promise<VoiceProfile> {
  const { data } = await supabase
    .from('mtn_shop_brand_voice').select('profile').eq('id', 'main').maybeSingle();
  return { ...DEFAULT_VOICE, ...pj<Partial<VoiceProfile>>(data?.profile as string | null, {}) };
}

export async function saveVoice(profile: VoiceProfile, note?: string): Promise<void> {
  const payload = JSON.stringify(profile);
  const { data: existing } = await supabase
    .from('mtn_shop_brand_voice').select('id').eq('id', 'main').maybeSingle();
  if (existing) {
    await supabase.from('mtn_shop_brand_voice')
      .update({ profile: payload, updated_at: new Date().toISOString() }).eq('id', 'main');
  } else {
    await supabase.from('mtn_shop_brand_voice').insert({ id: 'main', profile: payload });
  }
  await supabase.from('mtn_shop_voice_versions').insert({ profile: payload, note: note || 'Manual save' });
  await logEvent('voice_update', note || 'Brand voice profile retrained by admin.');
}

export async function fetchVoiceVersions(): Promise<{ id: string; profile: string; note: string | null; created_at: string }[]> {
  const { data } = await supabase
    .from('mtn_shop_voice_versions').select('*').order('created_at', { ascending: false }).limit(20);
  return (data || []) as { id: string; profile: string; note: string | null; created_at: string }[];
}

/** AI confidence score: grows as the brain accumulates knowledge + feedback. */
export function computeConfidence(opts: {
  products: number; inputs: number; approved: number; rejected: number; voiceTrained: boolean;
}): number {
  let score = 10;
  score += Math.min(30, (opts.products / 500) * 30);
  score += Math.min(25, opts.inputs * 2.5);
  score += Math.min(20, opts.approved * 2);
  score += Math.min(5, opts.rejected * 1);
  if (opts.voiceTrained) score += 10;
  return Math.min(99, Math.round(score));
}

export const HUB_BASE = '/socialpopadmin';
